/**
 * Trend Calculation Utilities
 * 
 * Functions for calculating trendlines and statistical measures.
 * 
 * NOTE: This is a template file. Replace with the actual trendCalculations.ts
 * from the ORBCoN Tools app (app/utils/trendCalculations.ts) for production use.
 */

/**
 * Calculate linear trendline using least squares method
 * Returns an object with trendline, upperBand, and lowerBand
 */
export function calculateTrendline(data: number[]): {
  trendline: number[];
  upperBand: number[];
  lowerBand: number[];
} {
  return calculateLinearTrendline(data);
}

/**
 * Calculate linear trendline with confidence bands
 * Returns an object with trendline, upper band, and lower band
 */
export function calculateLinearTrendline(
  data: number[],
  confidenceLevel: number = 0.95
): {
  trendline: number[];
  upperBand: number[];
  lowerBand: number[];
} {
  if (data.length === 0) {
    return {
      trendline: [],
      upperBand: [],
      lowerBand: [],
    };
  }

  // Calculate the base trendline using least squares method
  const n = data.length;
  const x = Array.from({ length: n }, (_, i) => i + 1);
  
  // Calculate means
  const xMean = x.reduce((a, b) => a + b, 0) / n;
  const yMean = data.reduce((a, b) => a + b, 0) / n;
  
  // Calculate slope and intercept
  let numerator = 0;
  let denominator = 0;
  
  for (let i = 0; i < n; i++) {
    numerator += (x[i] - xMean) * (data[i] - yMean);
    denominator += Math.pow(x[i] - xMean, 2);
  }
  
  const slope = denominator !== 0 ? numerator / denominator : 0;
  const intercept = yMean - slope * xMean;
  
  // Generate trendline values
  const trendline = x.map(xi => slope * xi + intercept);
  
  // Calculate residuals
  const residuals = data.map((y, i) => y - trendline[i]);
  const residualSumSquares = residuals.reduce((sum, r) => sum + r * r, 0);
  const degreesOfFreedom = data.length - 2;
  const residualVariance = degreesOfFreedom > 0 ? residualSumSquares / degreesOfFreedom : 0;
  const standardError = Math.sqrt(residualVariance);
  
  // Calculate t-value (approximate for 95% confidence, 2-tailed)
  // For simplicity, using 1.96 (normal distribution approximation)
  const tValue = confidenceLevel === 0.95 ? 1.96 : 2.0;
  
  // Calculate confidence bands
  const sumSquaredDeviations = x.reduce((sum, xi) => sum + Math.pow(xi - xMean, 2), 0);
  
  const upperBand = trendline.map((y, i) => {
    const xi = x[i];
    const deviation = Math.pow(xi - xMean, 2);
    const margin = tValue * standardError * Math.sqrt(1 / n + deviation / sumSquaredDeviations);
    return y + margin;
  });
  
  const lowerBand = trendline.map((y, i) => {
    const xi = x[i];
    const deviation = Math.pow(xi - xMean, 2);
    const margin = tValue * standardError * Math.sqrt(1 / n + deviation / sumSquaredDeviations);
    return y - margin;
  });
  
  return {
    trendline,
    upperBand,
    lowerBand,
  };
}

/**
 * Calculate percentage change between two values
 */
export function calculatePercentageChange(current: number, previous: number): number {
  if (previous === 0) return 0;
  return ((current - previous) / previous) * 100;
}

/**
 * Calculate average of array
 */
export function calculateAverage(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}
